<template>
 <section class="roleperms-page">
  <header-page :title="$route.meta.title || 'Permisos por rol'" />
  <div class="roleperms-content">
   <v-card class="roleperms-card" flat>
    <div v-if="saving || (loading && roleId)" class="roleperms-overlay">
     <v-progress-circular indeterminate color="primary" :size="46" :width="4" />
     <span>{{ saving ? 'Guardando permisos…' : 'Cargando…' }}</span>
    </div>
    <v-card-title class="roleperms-heading">
     <div class="roleperms-title-group">
      <span class="roleperms-title-icon"><v-icon aria-hidden="true">admin_panel_settings</v-icon></span>
      <div><span class="roleperms-eyebrow">ADMINISTRACIÓN</span><h1>Permisos por rol</h1></div>
     </div>
     <v-spacer />
     <app-button
      green
      text="Guardar permisos"
      width="190"
      height="38"
      :loading="saving"
      :disabled="saving || !roleId || !dirty"
      @click="askSave" />
    </v-card-title>

    <v-card-text>
     <v-alert :value="!!error" type="error">{{ error }}</v-alert>
     <v-alert :value="!!message" type="success">{{ message }}</v-alert>
     <v-progress-linear v-if="loading" indeterminate />

     <template v-if="allowed && !loading">
      <p class="roleperms-intro">
       Por cada <strong>recurso</strong> define el <strong>CRUD</strong>
       (<strong>C</strong> Crear · <strong>R</strong> Ver · <strong>U</strong> Editar · <strong>D</strong> Eliminar)
       y sus <strong>botones de acción</strong>. <strong>R (Ver)</strong> es la acción maestra:
       sin ella el recurso queda sin acceso (y sus botones se desactivan).
      </p>

      <div class="roleperms-toolbar">
       <div class="roleperms-role-select">
        <base-select
         :hide-details="false"
         v-model="roleId"
         :items="roles" item-text="name" item-value="id"
         label="Rol a configurar" :disabled="saving" @input="onRoleChange" />
       </div>
       <div v-if="roleId" class="roleperms-search">
        <v-text-field
         v-model="filterText" hide-details clearable
         prepend-inner-icon="search" label="Buscar módulo o recurso" />
       </div>
       <div v-if="roleId" class="roleperms-bulk">
        <v-tooltip top>
         <app-button
          slot="activator" gray :text="allCollapsed ? 'Expandir todo' : 'Contraer todo'"
          width="130" height="34" @click="toggleAllCollapse" />
         <span>Abre o cierra todas las tarjetas de módulos.</span>
        </v-tooltip>
        <v-tooltip top>
         <app-button
          slot="activator" green text="Marcar todo" width="120" height="34"
          :disabled="saving" @click="setAll(true)" />
         <span>Activa el CRUD y los botones de TODOS los recursos de todos los módulos.</span>
        </v-tooltip>
        <v-tooltip top>
         <app-button
          slot="activator" blue text="Quitar todo" width="120" height="34"
          :disabled="saving" @click="setAll(false)" />
         <span>Quita todos los permisos: deja cada recurso sin acceso.</span>
        </v-tooltip>
       </div>
      </div>

      <div v-if="!roleId" class="roleperms-placeholder">
       <span class="roleperms-placeholder-icon"><v-icon aria-hidden="true">badge</v-icon></span>
       <strong>Selecciona un rol para empezar</strong>
       <span>Elige un rol y define qué puede ver y hacer en cada módulo.</span>
      </div>

      <div v-if="roleId" class="roleperms-modules">
       <transition-group name="roleperms-fade" tag="div" class="roleperms-modules-group">
       <article v-for="mod in filteredModules" :key="mod.code"
        class="roleperms-module"
        :class="{ 'is-off': !moduleActive(mod), 'is-collapsed': collapsed[mod.code] }">
        <header class="roleperms-module-head">
         <button
          type="button" class="roleperms-module-collapse"
          :aria-label="'Expandir o contraer ' + mod.name"
          @click="toggleCollapse(mod.code)">
          <v-icon aria-hidden="true">
           {{ collapsed[mod.code] ? 'chevron_right' : 'expand_more' }}
          </v-icon>
         </button>
         <span class="roleperms-module-icon">
          <img v-if="mod.icon" :src="mod.icon" alt="" class="roleperms-module-iconimg" @error="onIconError(mod)">
          <v-icon v-else aria-hidden="true">folder_open</v-icon>
         </span>
         <div class="roleperms-module-title">
          <strong>{{ mod.name }}</strong>
          <div class="roleperms-module-meter">
           <span class="roleperms-module-meter-bar">
            <i :style="{ width: coverage(mod) + '%' }"></i>
           </span>
           <span class="roleperms-module-meter-text">
            {{ activeResources(mod) }}/{{ mod.resources.length }} con acceso
           </span>
          </div>
         </div>
         <v-tooltip top>
          <v-icon slot="activator" small class="roleperms-help">help_outline</v-icon>
          <span>Activa o desactiva el acceso (Ver) de todos los recursos del módulo.</span>
         </v-tooltip>
         <v-switch
          class="roleperms-module-switch" color="success" hide-details :disabled="saving"
          :input-value="moduleActive(mod)"
          :aria-label="'Acceso al módulo ' + mod.name"
          @change="toggleModule(mod, $event)" />
        </header>

        <div v-show="!collapsed[mod.code]" class="roleperms-reslist">
         <div v-for="res in mod.resources" :key="res.code"
          class="roleperms-res" :class="{ 'is-off': !isOn(res.code, 'read') }">
          <div class="roleperms-res-head">
           <span class="roleperms-res-name">
            {{ res.name }}
            <v-tooltip top>
             <v-icon slot="activator" small class="roleperms-help">help_outline</v-icon>
             <span>
              Marca <b>R (Ver)</b> para dar acceso. <b>C</b> crea, <b>U</b> edita,
              <b>D</b> elimina. Sin <b>R</b> todo el recurso queda sin acceso.
             </span>
            </v-tooltip>
           </span>
           <div class="roleperms-res-crud">
            <button
             v-for="a in actionCols" :key="a.code" type="button"
             class="roleperms-crud" :class="{ 'is-on': isOn(res.code, a.code), 'is-master': a.master }"
             :title="a.label" :aria-pressed="isOn(res.code, a.code) ? 'true' : 'false'"
             :aria-label="a.label + ' en ' + res.name"
             :disabled="saving || (a.code !== 'read' && !isOn(res.code, 'read'))"
             @click="toggleAction(res.code, a.code, !isOn(res.code, a.code))">
             <v-icon small aria-hidden="true">
              {{ isOn(res.code, a.code) ? 'check_box' : 'check_box_outline_blank' }}
             </v-icon>
             <span>{{ a.label }}</span>
            </button>
           </div>
          </div>

          <div v-if="res.buttons && res.buttons.length" class="roleperms-res-buttons">
           <span class="roleperms-res-buttons-label">
            <v-icon small aria-hidden="true">smart_button</v-icon>Acciones
            <v-tooltip top>
             <v-icon slot="activator" small class="roleperms-help">help_outline</v-icon>
             <span>Habilita los botones de acción del recurso (ej. crear, importar, exportar).</span>
            </v-tooltip>
           </span>
           <button
            v-for="btn in res.buttons" :key="btn.code" type="button"
            class="roleperms-btn" :class="{ 'is-on': isOn(res.code, btn.code) }"
            :aria-pressed="isOn(res.code, btn.code) ? 'true' : 'false'"
            :aria-label="btn.name + ' en ' + res.name"
            :disabled="saving || !isOn(res.code, 'read')"
            @click="toggleAction(res.code, btn.code, !isOn(res.code, btn.code))">
            <v-icon small aria-hidden="true">
             {{ isOn(res.code, btn.code) ? 'check_box' : 'check_box_outline_blank' }}
            </v-icon>
            <span>{{ btn.name }}</span>
           </button>
          </div>
         </div>
        </div>
       </article>
       </transition-group>
      </div>

      <div v-if="roleId" class="roleperms-footer">
       <div class="roleperms-summary" role="status" aria-live="polite">
        <span class="roleperms-summary-icon"><v-icon aria-hidden="true">verified_user</v-icon></span>
        <div>
         <strong>{{ enabledResourcesCount }} {{ enabledResourcesCount === 1 ? 'recurso habilitado' : 'recursos habilitados' }}</strong>
         <span>{{ dirty ? 'Tienes cambios sin guardar.' : 'Sin cambios pendientes.' }}</span>
        </div>
       </div>
       <app-button
        green
        text="Guardar permisos"
        width="200"
        height="44"
        :loading="saving"
        :disabled="saving || !dirty"
        @click="askSave" />
      </div>

      <p v-if="!roles.length" class="roleperms-empty">
       No hay roles disponibles para esta organización. Revísalos en ACL.
      </p>
     </template>

     <v-btn v-if="!loading && !ready" flat @click="load">Reintentar</v-btn>
    </v-card-text>
   </v-card>

   <v-dialog v-model="confirmOpen" persistent max-width="440">
    <v-card class="roleperms-confirm">
     <div class="roleperms-confirm-head">
      <span class="roleperms-confirm-icon"><v-icon aria-hidden="true">verified_user</v-icon></span>
      <div>
       <strong>¿Guardar permisos?</strong>
       <span>Se actualizarán los permisos del rol <b>{{ selectedRoleName }}</b>.</span>
      </div>
     </div>
     <div class="roleperms-confirm-body">
      <p class="roleperms-confirm-lead">
       Vas a <b>guardar los permisos</b> del rol <b>{{ selectedRoleName }}</b>. Esto
       <b>reemplaza por completo</b> lo que el rol puede ver y hacer en el sistema.
      </p>
      <div class="roleperms-confirm-stats">
       <div class="roleperms-confirm-stat">
        <strong>{{ saveSummary.modules }}</strong><span>módulos con acceso</span>
       </div>
       <div class="roleperms-confirm-stat">
        <strong>{{ saveSummary.resources }}</strong><span>recursos con acceso</span>
       </div>
       <div class="roleperms-confirm-stat">
        <strong>{{ saveSummary.crud }}</strong><span>acciones CRUD</span>
       </div>
       <div class="roleperms-confirm-stat">
        <strong>{{ saveSummary.buttons }}</strong><span>acciones</span>
       </div>
      </div>
      <div v-if="affectedModules.length" class="roleperms-confirm-list">
       <div v-for="m in affectedModules" :key="m.name" class="roleperms-confirm-row">
        <span class="roleperms-confirm-dot"></span>
        <span class="roleperms-confirm-row-name">{{ m.name }}</span>
        <span class="roleperms-confirm-row-count">{{ m.active }}/{{ m.total }} recursos</span>
       </div>
      </div>
      <p v-else class="roleperms-confirm-empty">
       <v-icon small aria-hidden="true">info</v-icon>
       Con esta configuración el rol quedará <b>sin acceso</b> a ningún módulo.
      </p>
     </div>
     <v-card-actions class="roleperms-confirm-actions">
      <v-btn flat :disabled="saving" @click="confirmOpen = false">Cancelar</v-btn>
      <v-spacer />
      <app-button
       green text="Sí, guardar" width="150" height="40"
       :loading="saving" @click="confirmSave" />
     </v-card-actions>
    </v-card>
   </v-dialog>
  </div>
 </section>
</template>

<script>
import BaseSelect from '@/components/shared/inputs/base-select';
// Ajusta la ruta a donde tengas tu componente global de boton:
import AppButton from '@/components/shared/app-button';
import {
	ACTIONS, ACTION_ORDER, normalizeSidebar, toPayload, toResourcesMap, fromResourcesMap,
	applyMenuOrder,
} from '@/shared/permissions-catalog';

export default {
	name: 'page-role-permissions',
	components: { BaseSelect, AppButton },
	data() {
		return {
			allowed: false,
			loading: false,
			saving: false,
			ready: false,
			error: '',
			message: '',
			roles: [],
			roleId: null,
			modules: [],
			actionCols: ACTION_ORDER.map(code => ACTIONS[code]),
			permMap: {},
			savedSnapshot: '',
			codeCompany: null,
			filterText: '',
			collapsed: {},
			confirmOpen: false,
		};
	},
	created() { this.load(); },
	computed: {
		dirty() {
			return JSON.stringify(toPayload(this.modules, this.permMap)) !== this.savedSnapshot;
		},
		enabledResourcesCount() {
			return Object.keys(this.permMap).filter(code => this.isOn(code, 'read')).length;
		},
		totalActions() {
			return toPayload(this.modules, this.permMap)
				.reduce((n, p) => n + p.actions.length, 0);
		},
		selectedRoleName() {
			const role = this.roles.find(r => r.id === this.roleId);
			return (role && role.name) || '';
		},
		saveSummary() {
			const payload = toPayload(this.modules, this.permMap);
			const crudSet = new Set(ACTION_ORDER);
			let crud = 0;
			let buttons = 0;
			payload.forEach((p) => {
				p.actions.forEach((a) => {
					if (crudSet.has(a)) crud += 1;
					else buttons += 1;
				});
			});
			const mods = new Set(payload.map(p => p.module));
			return {
				modules: mods.size,
				resources: payload.length,
				crud,
				buttons,
				actions: crud + buttons,
			};
		},
		affectedModules() {
			return this.modules
				.map(m => ({
					name: m.name,
					active: this.activeResources(m),
					total: m.resources.length,
				}))
				.filter(m => m.active > 0);
		},
		allCollapsed() {
			return this.modules.length > 0
				&& this.modules.every(m => this.collapsed[m.code]);
		},
		filteredModules() {
			const q = (this.filterText || '').trim().toLowerCase();
			if (!q) return this.modules;
			return this.modules
				.map((mod) => {
					if (mod.name && mod.name.toLowerCase().includes(q)) return mod;
					const resources = mod.resources
						.filter(res => res.name && res.name.toLowerCase().includes(q));
					return { ...mod, resources };
				})
				.filter(mod => mod.resources.length);
		},
	},
	methods: {
		onIconError(mod) { this.$set(mod, 'icon', ''); },
		askSave() {
			if (!this.roleId || !this.dirty || this.saving) return;
			this.confirmOpen = true;
		},
		confirmSave() {
			this.confirmOpen = false;
			this.savePermissions();
		},
		coverage(mod) {
			if (!mod.resources.length) return 0;
			return Math.round((this.activeResources(mod) / mod.resources.length) * 100);
		},
		toggleCollapse(code) {
			this.$set(this.collapsed, code, !this.collapsed[code]);
		},
		toggleAllCollapse() {
			const anyOpen = this.modules.some(m => !this.collapsed[m.code]);
			const map = {};
			this.modules.forEach((m) => { map[m.code] = anyOpen; });
			this.collapsed = map;
		},
		isOn(resourceCode, actionCode) {
			const set = this.permMap[resourceCode];
			return !!(set && set.has(actionCode));
		},
		moduleActive(mod) {
			return mod.resources.some(res => this.isOn(res.code, 'read'));
		},
		activeResources(mod) {
			return mod.resources.filter(res => this.isOn(res.code, 'read')).length;
		},
		toggleAction(resourceCode, actionCode, value) {
			const set = new Set(this.permMap[resourceCode] || []);
			if (actionCode === 'read') {
				if (value) set.add('read');
				else set.clear(); // sin Ver => sin CRUD ni botones
			} else if (value) {
				set.add(actionCode);
				set.add('read'); // cualquier acción/botón implica poder Ver
			} else {
				set.delete(actionCode);
			}
			this.$set(this.permMap, resourceCode, set);
		},
		toggleModule(mod, value) {
			mod.resources.forEach((res) => { this.toggleAction(res.code, 'read', value); });
		},
		setAll(value) {
			const map = {};
			this.modules.forEach((mod) => {
				mod.resources.forEach((res) => {
					const all = ACTION_ORDER.concat((res.buttons || []).map(b => b.code));
					map[res.code] = value ? new Set(all) : new Set();
				});
			});
			this.permMap = map;
		},
		report(errorOrResponse) {
			const response = (errorOrResponse && errorOrResponse.response) || errorOrResponse || {};
			this.error = (response.data && response.data.message)
				|| 'No se pudo completar la operación. Intenta nuevamente.';
		},
		// Carga los botones de acción de cada recurso desde ACL, mismo endpoint que app-actions:
		// GET sidebar/{APP_CODE}/resource/{uri}. El código del botón es `code` y el nombre
		// visible es `languages` (se excluye 'HELP', que es el botón de ayuda, no un permiso).
		async loadButtons() {
			const appCode = process.env.APP_CODE;
			const params = { languages: 'es' };
			const headers = { Accept: 'application/vnd.apiacl.v2+json' };
			const resources = [];
			this.modules.forEach((mod) => {
				mod.resources.forEach((res) => { resources.push(res); });
			});
			await Promise.all(resources.map(async (res) => {
				try {
					const { data } = await this.$httpAcl.get(
						`sidebar/${appCode}/resource/${res.code}`,
						{ params, headers },
					);
					const list = (data && data.data) || [];
					const buttons = list
						.filter(a => a.code && a.code !== 'HELP')
						.map(a => ({ code: a.code, name: a.languages || a.name || a.code }));
					this.$set(res, 'buttons', buttons);
				} catch (err) {
					this.$set(res, 'buttons', res.buttons || []);
				}
			}));
		},
		async load() {
			this.loading = true; this.ready = false; this.allowed = false;
			this.error = ''; this.message = '';
			try {
				const { data: session } = await this.$http.get('company-token');
				this.allowed = !!(session.auxiliary
					&& [true, 1].includes(session.auxiliary.flagAccountant));
				if (!this.allowed) {
					this.error = 'Solo el contador administrador puede configurar permisos por rol.';
					return;
				}
				this.codeCompany = session.companyAcl && session.companyAcl.code_company;
				if (!this.codeCompany) { this.error = 'No se pudo determinar la organización de la sesión.'; return; }
				const [rolesRes, sidebarRes] = await Promise.all([
					this.$httpAcl.get(`company/${this.codeCompany}/role`),
					this.$httpAcl.get(`sidebar/${process.env.APP_CODE}`),
				]);
				const rolesData = rolesRes.data;
				this.roles = Array.isArray(rolesData) ? rolesData : (rolesData.data || []);
				// Mismo orden que el menú lateral (por position).
				this.modules = applyMenuOrder(
					normalizeSidebar(sidebarRes.data.data),
					sidebarRes.data.data,
				);
				await this.loadButtons();
				this.ready = true;
			} catch (error) { this.report(error); } finally { this.loading = false; }
		},
		async onRoleChange() {
			if (!this.roleId) return;
			this.loading = true; this.error = ''; this.message = '';
			try {
				// Estándar del rol desde ACL: company/{codeCompany}/role/{roleId}/permission
				const { data } = await this.$httpAcl.get(
					`company/${this.codeCompany}/role/${this.roleId}/permission`,
				);
				const permission = (data && data.data && data.data.permission) || {};
				this.permMap = fromResourcesMap(permission.resources || {});
				this.savedSnapshot = JSON.stringify(toPayload(this.modules, this.permMap));
			} catch (error) {
				if (error.response && error.response.status === 404) {
					this.permMap = fromResourcesMap({});
					this.savedSnapshot = JSON.stringify(toPayload(this.modules, this.permMap));
				} else {
					this.report(error);
				}
			} finally { this.loading = false; }
		},
		async savePermissions() {
			if (this.saving || !this.roleId || !this.dirty) return;
			this.saving = true; this.error = ''; this.message = '';
			try {
				const summary = this.saveSummary;
				const payload = { permission: { resources: toResourcesMap(this.modules, this.permMap) } };
				// Guardar estándar del rol en ACL: role/{roleId}/permission
				await this.$httpAcl.patch(`role/${this.roleId}/permission`, payload);
				this.savedSnapshot = JSON.stringify(toPayload(this.modules, this.permMap));
				this.message = `Permisos del rol "${this.selectedRoleName}" guardados: `
					+ `${summary.modules} módulo(s), ${summary.resources} recurso(s) con acceso, `
					+ `${summary.crud} acción(es) CRUD y ${summary.buttons} botón(es).`;
				// Recargamos para sincronizar menú y permisos con lo recién guardado.
				setTimeout(() => { window.location.reload(); }, 1200);
			} catch (error) { this.report(error); } finally { this.saving = false; }
		},
	},
};
</script>

<style lang="scss">
.roleperms-page {
	background: color(background-table);
	min-height: calc(100vh - 80px);
	color: color(text);
	font-family: font(AvenirRegular);
}
.roleperms-content { max-width: 1760px; padding: 28px 32px; margin: auto; }
.roleperms-card { position: relative; border: 1px solid color(grey-divider); border-radius: 12px; overflow: hidden; }
.roleperms-heading {
	gap: 12px; padding: 24px; border-bottom: 1px solid color(grey-divider);
	h1 { background: transparent !important; color: color(black); font-family: font(AvenirBold); font-size: 24px; margin: 4px 0 0; padding: 0; }
}
.roleperms-eyebrow { color: color(secondary); font-size: 11px; letter-spacing: 1.5px; }
.roleperms-title-group { display: flex; align-items: center; gap: 16px; }
.roleperms-title-icon { display: flex; align-items: center; justify-content: center; width: 48px; height: 48px; border-radius: 14px; background: #e8f5f6; }
.roleperms-page .roleperms-title-icon .v-icon { color: #008e95 !important; font-size: 28px; }
.roleperms-card .v-card__text { padding: 24px; }
.roleperms-card .v-btn { border-radius: 6px; text-transform: none; letter-spacing: normal; }
.roleperms-intro { font-size: 13px; line-height: 1.6; margin: 0 0 18px; color: #4a5a63; }

.roleperms-toolbar { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 18px; }
.roleperms-role-select { width: 320px; max-width: 100%; }
.roleperms-bulk { display: flex; gap: 8px; }
.roleperms-page .roleperms-bulk-btn { border: 1px solid #c6d8e7; color: #326282 !important; background: #fff !important; height: 34px; }
.roleperms-page .roleperms-bulk-btn .v-icon { color: inherit !important; }

.roleperms-modules { display: flex; flex-direction: column; gap: 14px; }
.roleperms-module { border: 1px solid #dce6eb; border-radius: 12px; overflow: hidden; background: #fff; }
.roleperms-module.is-off { background: #fafbfc; }
.roleperms-module-head { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: #f4f8fa; border-bottom: 1px solid #e3edf1; }
.roleperms-module-icon { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 38px; height: 38px; border-radius: 10px; background: #0a9ca2; }
.roleperms-module-iconimg { width: 18px; height: 18px; object-fit: contain; filter: brightness(0) invert(1); opacity: .95; }
.roleperms-page .roleperms-module-icon .v-icon { color: #fff !important; font-size: 20px; }
.roleperms-module.is-off .roleperms-module-icon { background: #aeb9c1; }
.roleperms-module-title { flex: 1; min-width: 0; }
.roleperms-module-title strong { display: block; color: #22323b; font-size: 14px; font-weight: 700; }
.roleperms-module-title span { display: block; margin-top: 1px; color: #6b7a84; font-size: 11px; }
.roleperms-module-switch { flex: 0 0 auto; margin: 0; }

.roleperms-reslist { display: flex; flex-direction: column; }
.roleperms-res { padding: 12px 16px; border-bottom: 1px solid #f0f3f5; }
.roleperms-res:last-child { border-bottom: 0; }
.roleperms-res.is-off { background: #fbfcfd; }
.roleperms-res-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.roleperms-res-name { font-size: 13px; font-weight: 600; color: #35474f; }
.roleperms-res.is-off .roleperms-res-name { color: #9aa6b0; }
.roleperms-res-crud { display: flex; flex-wrap: wrap; gap: 8px; }
.roleperms-crud { display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px; border: 1px solid #e3eaee; border-radius: 8px; background: #fff; cursor: pointer; font: inherit; line-height: 1; transition: all .15s ease; }
.roleperms-crud span { font-size: 12px; font-weight: 600; color: #7b8a94; white-space: nowrap; }
.roleperms-crud span b { font-weight: 800; }
.roleperms-page .roleperms-crud .v-icon { color: #b4c0c9 !important; font-size: 18px !important; }
.roleperms-crud.is-on { border-color: #bfe6cf; background: #eefaf1; }
.roleperms-crud.is-on span { color: #0b6f68; }
.roleperms-page .roleperms-crud.is-on .v-icon { color: #16a085 !important; }
.roleperms-crud:disabled { cursor: not-allowed; opacity: .45; }
.roleperms-crud:not(:disabled):hover { border-color: #9fd9c3; transform: translateY(-1px); }
.roleperms-crud.is-master.is-on { border-color: #16a085; box-shadow: inset 0 0 0 1px #c4ecd9; }

.roleperms-res-buttons { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #e7edf1; }
.roleperms-res-buttons-label { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: #8a98a2; text-transform: uppercase; letter-spacing: .3px; margin-right: 4px; }
.roleperms-page .roleperms-res-buttons-label .v-icon { color: #8a98a2 !important; font-size: 15px; }
.roleperms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 4px 12px 4px 8px; border: 1px solid #d7e2ea; border-radius: 999px; cursor: pointer; background: #fff; font: inherit; line-height: 1; transition: all .15s ease; }
.roleperms-btn span { font-size: 12px; color: #4a5a63; }
.roleperms-page .roleperms-btn .v-icon { color: #b4c0c9 !important; font-size: 17px !important; }
.roleperms-btn.is-on { border-color: #9fd9c3; background: #eafaf2; }
.roleperms-btn.is-on span { color: #0b6f68; font-weight: 600; }
.roleperms-page .roleperms-btn.is-on .v-icon { color: #16a085 !important; }
.roleperms-btn:disabled { cursor: not-allowed; opacity: .5; }
.roleperms-btn:not(:disabled):hover { border-color: #16a085; box-shadow: 0 2px 6px rgba(22, 160, 133, 0.15); transform: translateY(-1px); }
.roleperms-res.is-off .roleperms-res-buttons { opacity: .55; }

.roleperms-footer { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-top: 20px; padding: 18px 20px; border: 1px solid #dce6eb; border-radius: 10px; background: #f8fafc; }
.roleperms-summary { display: flex; align-items: center; gap: 12px; }
.roleperms-summary strong { display: block; color: #2c404a; font-size: 14px; }
.roleperms-summary > div > span { display: block; margin-top: 4px; color: #607582; font-size: 12px; }
.roleperms-summary-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 40px; height: 40px; border-radius: 10px; background: #e8f5f6; }
.roleperms-page .roleperms-summary-icon .v-icon { color: #008e95 !important; }

.roleperms-page .roleperms-save-action {
	height: 46px; margin: 0; padding: 0 22px; flex-shrink: 0;
	border: 1px solid rgba(255, 255, 255, 0.18); border-radius: 12px;
	background: linear-gradient(135deg, #00888e 0%, #00686f 100%) !important;
	box-shadow: 0 4px 12px rgba(0, 104, 111, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.15);
	font-weight: 600; transition: transform 160ms ease, box-shadow 160ms ease;
}
.roleperms-page .roleperms-save-action .v-icon { box-sizing: content-box; padding: 6px; margin-right: 10px; border-radius: 8px; background: rgba(255, 255, 255, 0.14); }
.roleperms-page .roleperms-primary-action, .roleperms-page .roleperms-primary-action .v-icon { color: #fff !important; }
.roleperms-page .roleperms-save-action:not(.v-btn--disabled):hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(0, 104, 111, 0.3); }
.roleperms-page .roleperms-save-action.v-btn--disabled { opacity: 0.65; }
.roleperms-page .roleperms-heading .roleperms-save-action { height: 38px; }
.roleperms-empty { margin-top: 20px; color: color(error); font-size: 13px; }

.roleperms-search { flex: 1 1 240px; min-width: 200px; max-width: 420px; }

.roleperms-placeholder { display: flex; flex-direction: column; align-items: center; gap: 6px; text-align: center; padding: 48px 24px; border: 1px dashed #cdd9e0; border-radius: 12px; background: #f9fbfc; }
.roleperms-placeholder-icon { display: flex; align-items: center; justify-content: center; width: 56px; height: 56px; border-radius: 16px; background: #e8f5f6; margin-bottom: 6px; }
.roleperms-page .roleperms-placeholder-icon .v-icon { color: #008e95 !important; font-size: 30px; }
.roleperms-placeholder strong { color: #2c404a; font-size: 15px; }
.roleperms-placeholder span { color: #6b7a84; font-size: 13px; }

.roleperms-modules-group { column-count: 2; column-gap: 14px; }
.roleperms-module { transition: box-shadow .2s ease, border-color .2s ease; break-inside: avoid; -webkit-column-break-inside: avoid; page-break-inside: avoid; margin-bottom: 14px; }
.roleperms-module:hover { border-color: #bcd5db; box-shadow: 0 6px 18px rgba(10, 60, 70, 0.07); }
.roleperms-module.is-collapsed { background: #fcfdfe; }

.roleperms-module-collapse { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 8px; background: transparent; cursor: pointer; transition: background .15s ease; }
.roleperms-module-collapse:hover { background: #e3edf1; }
.roleperms-page .roleperms-module-collapse .v-icon { color: #6b7a84 !important; }

.roleperms-module-meter { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
.roleperms-module-meter-bar { position: relative; flex: 0 0 90px; height: 5px; border-radius: 999px; background: #e3edf1; overflow: hidden; }
.roleperms-module-meter-bar i { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px; background: linear-gradient(90deg, #16a085, #0a9ca2); transition: width .35s ease; }
.roleperms-module-meter-text { font-size: 11px; color: #6b7a84; }
.roleperms-module.is-off .roleperms-module-meter-bar i { background: #c3ccd3; }

.roleperms-res { transition: background .15s ease; }
.roleperms-res:hover { background: #f7fbfb; }
.roleperms-crud.is-on, .roleperms-btn.is-on { animation: roleperms-pop .18s ease; }
@keyframes roleperms-pop { 0% { transform: scale(.92); } 60% { transform: scale(1.04); } 100% { transform: scale(1); } }

.roleperms-fade-enter-active, .roleperms-fade-leave-active { transition: opacity .25s ease, transform .25s ease; }
.roleperms-fade-enter, .roleperms-fade-leave-to { opacity: 0; transform: translateY(-4px); }

.roleperms-footer { position: sticky; bottom: 12px; z-index: 3; box-shadow: 0 6px 20px rgba(20, 50, 60, 0.08); }

.roleperms-page .roleperms-help { color: #aab6bf !important; font-size: 15px !important; cursor: help; margin-left: 4px; vertical-align: middle; transition: color .15s ease; }
.roleperms-page .roleperms-help:hover { color: #008e95 !important; }

.roleperms-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 20; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; background: rgba(255, 255, 255, 0.76); }
.roleperms-overlay span { font-size: 13px; font-weight: 600; color: #35474f; letter-spacing: .2px; }

.roleperms-confirm { border-radius: 14px !important; overflow: hidden; }
.roleperms-confirm-head { display: flex; gap: 14px; padding: 22px 22px 8px; }
.roleperms-confirm-icon { flex: 0 0 44px; height: 44px; border-radius: 12px; background: #e8f5f6; display: flex; align-items: center; justify-content: center; }
.roleperms-confirm-icon .v-icon { color: #008e95 !important; font-size: 24px; }
.roleperms-confirm-head strong { display: block; font-size: 16px; color: #22323b; }
.roleperms-confirm-head > div > span { display: block; margin-top: 4px; font-size: 13px; line-height: 1.5; color: #6b7a84; }
.roleperms-confirm-body { padding: 10px 22px 4px; }
.roleperms-confirm-lead { margin: 0 0 14px; font-size: 13px; line-height: 1.55; color: #4a5a63; }
.roleperms-confirm-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
.roleperms-confirm-stat { text-align: center; padding: 12px 8px; border: 1px solid #e3edf1; border-radius: 10px; background: #f8fafc; }
.roleperms-confirm-stat strong { display: block; font-size: 22px; font-weight: 700; color: #0b6f68; }
.roleperms-confirm-stat span { font-size: 11px; color: #6b7a84; }
.roleperms-confirm-list { margin-top: 12px; max-height: 164px; overflow-y: auto; border: 1px solid #eef2f4; border-radius: 10px; }
.roleperms-confirm-row { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid #f2f5f6; }
.roleperms-confirm-row:last-child { border-bottom: 0; }
.roleperms-confirm-dot { flex: 0 0 8px; width: 8px; height: 8px; border-radius: 50%; background: #16a085; }
.roleperms-confirm-row-name { flex: 1; font-size: 13px; color: #35474f; }
.roleperms-confirm-row-count { font-size: 11px; font-weight: 600; color: #0b6f68; background: #eafaf2; border-radius: 999px; padding: 2px 8px; }
.roleperms-confirm-empty { display: flex; align-items: center; gap: 6px; margin: 14px 0 4px; font-size: 13px; color: #7b8a94; }
.roleperms-confirm-empty .v-icon { color: #c08a2b !important; }
.roleperms-confirm-actions { padding: 12px 18px 16px; }

@media (max-width: 1180px) {
	.roleperms-modules-group { column-count: 1; }
}

@media (max-width: 768px) {
	.roleperms-content { padding: 16px 12px; }
	.roleperms-heading { padding: 16px; }
	.roleperms-heading h1 { font-size: 21px; }
	.roleperms-card .v-card__text { padding: 16px; }
	.roleperms-role-select { width: 100%; }
	.roleperms-footer { flex-direction: column; align-items: stretch; }
	.roleperms-page .roleperms-save-action { width: 100%; }
}
</style>



// WEBPACK FOOTER //
// src/pages/users/role-permissions.vue